import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useCallback } from 'react';
import { toast } from 'sonner';

// Types
interface Task {
  id: string;
  sheetId: string;
  projectId: string;
  rowNumber: number;
  parentId: string | null;
  outlineLevel: number;
  outlineNumber: string;
  position: number;
  isSummary: boolean;
  isExpanded: boolean;
  name: string;
  description: string | null;
  startDate: string | null;
  finishDate: string | null;
  duration: number;
  durationUnit: string;
  percentComplete: number;
  status: string | null;
  priority: string | number;
  isCritical: boolean;
  customFields: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface CreateTaskDto {
  name: string;
  parentId?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  duration?: number;
  percentComplete?: number;
  status?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  isSummary?: boolean;
}

interface UpdateTaskDto {
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  duration?: number;
  percentComplete?: number;
  status?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  isSummary?: boolean;
  customFields?: Record<string, unknown>;
}

interface MoveTaskDto {
  parentId?: string | null;
  afterTaskId?: string | null;
}

interface BatchUpdateItem {
  id: string;
  data: UpdateTaskDto;
}

interface TaskQueryOptions {
  parentId?: string;
  includeSubtasks?: boolean;
  search?: string;
}

// Query keys
export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (sheetId: string, options?: TaskQueryOptions) =>
    [...taskKeys.lists(), sheetId, options] as const,
  bySheet: (sheetId: string) => [...taskKeys.lists(), sheetId] as const,
  detail: (taskId: string) => [...taskKeys.all, 'detail', taskId] as const,
};

// Helper: find the actual cache key for a sheet's task list (may include search options)
function findTaskListCacheKey(queryClient: ReturnType<typeof useQueryClient>, sheetId: string) {
  const queryCache = queryClient.getQueryCache();
  const match = queryCache.findAll({ queryKey: taskKeys.bySheet(sheetId) })[0];
  return match?.queryKey || taskKeys.list(sheetId);
}

function extractTasksFromResponse(payload: unknown): Task[] {
  if (Array.isArray(payload)) {
    return payload as Task[];
  }

  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;

    if (Array.isArray(obj.tasks)) {
      return obj.tasks as Task[];
    }

    if (Array.isArray(obj.data)) {
      return obj.data as Task[];
    }
  }

  return [];
}

// Fetch tasks for a sheet
export function useTasksQuery(sheetId: string, options?: TaskQueryOptions) {
  return useQuery({
    queryKey: taskKeys.list(sheetId, options),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.parentId) params.append('parentId', options.parentId);
      if (options?.includeSubtasks !== undefined) {
        params.append('includeSubtasks', String(options.includeSubtasks));
      }
      if (options?.search) params.append('search', options.search);

      const queryString = params.toString();
      const url = `/sheets/${sheetId}/tasks${queryString ? `?${queryString}` : ''}`;
      const response = await api.get<unknown>(url);
      return extractTasksFromResponse(response.data);
    },
    enabled: !!sheetId,
    staleTime: 60000, // 1 minute — WebSocket invalidates on changes
  });
}

// Fetch single task
export function useTaskQuery(taskId: string) {
  return useQuery({
    queryKey: taskKeys.detail(taskId),
    queryFn: async () => {
      const response = await api.get<Task>(`/tasks/${taskId}`);
      return response.data;
    },
    enabled: !!taskId,
  });
}

// Create task mutation with optimistic update
export function useCreateTask(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateTaskDto) => {
      const response = await api.post<Task>(`/sheets/${sheetId}/tasks`, dto);
      return response.data;
    },
    onMutate: async (newTask) => {
      // Cancel outgoing refetches for this sheet
      await queryClient.cancelQueries({ queryKey: taskKeys.bySheet(sheetId) });

      // Find the actual cache entry (may include search options in the key)
      const queryCache = queryClient.getQueryCache();
      const matchingQuery = queryCache.findAll({ queryKey: taskKeys.bySheet(sheetId) })[0];
      const cacheKey = matchingQuery?.queryKey || taskKeys.list(sheetId);

      // Snapshot previous value
      const previousTasks = queryClient.getQueryData<Task[]>(cacheKey);

      // Optimistically add the new task
      if (previousTasks) {
        const optimisticTask: Task = {
          id: `temp-${Date.now()}`,
          sheetId,
          projectId: '',
          rowNumber: previousTasks.length + 1,
          parentId: newTask.parentId || null,
          outlineLevel: 1,
          outlineNumber: String(previousTasks.length + 1),
          position: previousTasks.length + 1,
          isSummary: newTask.isSummary || false,
          isExpanded: true,
          name: newTask.name,
          description: newTask.description || null,
          startDate: newTask.startDate || null,
          finishDate: newTask.endDate || null,
          duration: newTask.duration || 1,
          durationUnit: 'd',
          percentComplete: newTask.percentComplete || 0,
          status: newTask.status || null,
          priority: 'normal',
          isCritical: false,
          customFields: {},
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        queryClient.setQueryData<Task[]>(cacheKey, [
          ...previousTasks,
          optimisticTask,
        ]);
      }

      return { previousTasks, cacheKey };
    },
    onError: (_err, _newTask, context) => {
      // Rollback on error
      if (context?.previousTasks && context?.cacheKey) {
        queryClient.setQueryData(context.cacheKey, context.previousTasks);
      }
      toast.error('Failed to create task');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: taskKeys.bySheet(sheetId) });
    },
  });
}

// Update task mutation with optimistic update
export function useUpdateTask(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      updates,
    }: {
      taskId: string;
      updates: UpdateTaskDto;
    }) => {
      const response = await api.patch<Task>(`/tasks/${taskId}`, updates);
      return response.data;
    },
    onMutate: async ({ taskId, updates }) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.bySheet(sheetId) });
      await queryClient.cancelQueries({ queryKey: taskKeys.detail(taskId) });

      const cacheKey = findTaskListCacheKey(queryClient, sheetId);
      const previousTasks = queryClient.getQueryData<Task[]>(cacheKey);
      const previousTask = queryClient.getQueryData<Task>(
        taskKeys.detail(taskId)
      );

      // Optimistically update in list
      if (previousTasks) {
        queryClient.setQueryData<Task[]>(
          cacheKey,
          previousTasks.map((task) =>
            task.id === taskId
              ? { ...task, ...updates, updatedAt: new Date().toISOString() }
              : task
          )
        );
      }

      // Optimistically update detail cache
      if (previousTask) {
        queryClient.setQueryData<Task>(taskKeys.detail(taskId), {
          ...previousTask,
          ...updates,
          updatedAt: new Date().toISOString(),
        });
      }

      return { previousTasks, previousTask, cacheKey };
    },
    onError: (_err, { taskId }, context) => {
      if (context?.previousTasks && context?.cacheKey) {
        queryClient.setQueryData(context.cacheKey, context.previousTasks);
      }
      if (context?.previousTask) {
        queryClient.setQueryData(taskKeys.detail(taskId), context.previousTask);
      }
    },
    onSettled: (_data, _err, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.bySheet(sheetId) });
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
    },
  });
}

// Delete task mutation with optimistic update
export function useDeleteTask(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      deleteSubtasks = false,
    }: {
      taskId: string;
      deleteSubtasks?: boolean;
    }) => {
      await api.delete(`/tasks/${taskId}?deleteSubtasks=${deleteSubtasks}`);
      return taskId;
    },
    onMutate: async ({ taskId }) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.bySheet(sheetId) });

      const cacheKey = findTaskListCacheKey(queryClient, sheetId);
      const previousTasks = queryClient.getQueryData<Task[]>(cacheKey);

      if (previousTasks) {
        queryClient.setQueryData<Task[]>(
          cacheKey,
          previousTasks.filter((task) => task.id !== taskId)
        );
      }

      return { previousTasks, cacheKey };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousTasks && context?.cacheKey) {
        queryClient.setQueryData(context.cacheKey, context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.bySheet(sheetId) });
    },
  });
}

// Move task mutation
export function useMoveTask(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      move,
    }: {
      taskId: string;
      move: MoveTaskDto;
    }) => {
      const response = await api.post<Task>(`/tasks/${taskId}/move`, move);
      return response.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.bySheet(sheetId) });
    },
  });
}

// Indent task mutation
export function useIndentTask(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      const response = await api.post<Task>(`/tasks/${taskId}/indent`);
      return response.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.bySheet(sheetId) });
    },
  });
}

// Outdent task mutation
export function useOutdentTask(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      const response = await api.post<Task>(`/tasks/${taskId}/outdent`);
      return response.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.bySheet(sheetId) });
    },
  });
}

// Batch update tasks
export function useBatchUpdateTasks(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: BatchUpdateItem[]) => {
      const response = await api.patch<{ updated: Task[]; failed: string[] }>(
        `/sheets/${sheetId}/tasks/batch`,
        { updates }
      );
      return response.data;
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.bySheet(sheetId) });

      const cacheKey = findTaskListCacheKey(queryClient, sheetId);
      const previousTasks = queryClient.getQueryData<Task[]>(cacheKey);

      if (previousTasks) {
        const updateMap = new Map(updates.map((u) => [u.id, u.data]));
        queryClient.setQueryData<Task[]>(
          cacheKey,
          previousTasks.map((task) => {
            const taskUpdates = updateMap.get(task.id);
            return taskUpdates
              ? { ...task, ...taskUpdates, updatedAt: new Date().toISOString() }
              : task;
          })
        );
      }

      return { previousTasks, cacheKey };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousTasks && context?.cacheKey) {
        queryClient.setQueryData(context.cacheKey, context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.bySheet(sheetId) });
    },
  });
}

// Batch delete tasks
export function useBatchDeleteTasks(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskIds: string[]) => {
      const response = await api.delete<{ deleted: string[]; failed: string[] }>(
        `/sheets/${sheetId}/tasks/batch`,
        { data: { taskIds } }
      );
      return response.data;
    },
    onMutate: async (taskIds) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.bySheet(sheetId) });

      const cacheKey = findTaskListCacheKey(queryClient, sheetId);
      const previousTasks = queryClient.getQueryData<Task[]>(cacheKey);

      if (previousTasks) {
        const idsToDelete = new Set(taskIds);
        queryClient.setQueryData<Task[]>(
          cacheKey,
          previousTasks.filter((task) => !idsToDelete.has(task.id))
        );
      }

      return { previousTasks, cacheKey };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousTasks && context?.cacheKey) {
        queryClient.setQueryData(context.cacheKey, context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.bySheet(sheetId) });
    },
  });
}

// Custom hook for cell value updates with debouncing
export function useUpdateCell(sheetId: string) {
  const updateTask = useUpdateTask(sheetId);
  const queryClient = useQueryClient();

  const updateCell = useCallback(
    async (taskId: string, field: string, value: unknown) => {
      // Handle custom fields vs built-in fields
      const isCustomField = field.startsWith('customFields.');
      const updates: UpdateTaskDto = {};

      if (isCustomField) {
        const customFieldKey = field.replace('customFields.', '');
        const previousTasks = queryClient.getQueryData<Task[]>(
          taskKeys.list(sheetId)
        );
        const task = previousTasks?.find((t) => t.id === taskId);
        updates.customFields = {
          ...(task?.customFields || {}),
          [customFieldKey]: value,
        };
      } else {
        (updates as Record<string, unknown>)[field] = value;
      }

      return updateTask.mutateAsync({ taskId, updates });
    },
    [updateTask, queryClient, sheetId]
  );

  return {
    updateCell,
    isUpdating: updateTask.isPending,
    error: updateTask.error,
  };
}
